import { LONDON_NEIGHBORHOODS } from '../../../../shared/londonNeighborhoods.cjs';
import { getCityPriceRange, getCitySeo } from '../../../../shared/citySeoData.cjs';
import { cityInfo } from '../data/cityData.js';

/**
 * Devuelve un esquema para la organización Coolliving
 * @param {string} siteUrl - URL del sitio
 * @returns {Object} Esquema de organización
 */
const getColivingOrganizationSchema = (siteUrl) => ({
  '@type': 'Organization',
  '@id': `${siteUrl}/#organization`,
  name: 'Coolliving',
  alternateName: 'Cool Living',
  url: siteUrl,
  logo: {
    '@type': 'ImageObject',
    url: `${siteUrl}/images/favicon.jpg`,
    width: 192,
    height: 192
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+34910000000',
    email: 'soporte@coolliving.io',
    contactType: 'customer service',
    availableLanguage: ['Spanish', 'English']
  },
  sameAs: [
    'https://www.linkedin.com/company/coolliving',
    'https://twitter.com/coolliving',
    'https://www.instagram.com/coolliving'
  ],
  description: 'Plataforma líder para alquiler de habitaciones, coliving, apartamentos amueblados y alquiler flexible en España'
});

/**
 * Get website schema específico para búsqueda de colivings
 * @param {string} siteUrl - URL del sitio
 * @returns {Array<Object>} - Schema combinado
 */
export const getColivingSearchWebsiteSchema = (siteUrl) => {
  const orgSchema = getColivingOrganizationSchema(siteUrl);
  
  const websiteSchema = {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name: 'Coolliving - Habitaciones, Coliving y Apartamentos Amueblados',
    description: 'Descubre habitaciones en alquiler, colivings y apartamentos amueblados en toda España. Busca por ubicación, precio y características. Alquiler flexible desde 1 mes, todo incluido.',
    publisher: {
      '@id': `${siteUrl}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/encuentratucoliving/{search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
  
  return [orgSchema, websiteSchema];
};

/**
 * Get service schema específico para colivings
 * @param {string} siteUrl - URL del sitio
 * @returns {Array<Object>} Schema combinado
 */
export const getColivingServiceSchema = (siteUrl) => {
  const orgSchema = getColivingOrganizationSchema(siteUrl);
  
  const serviceSchema = {
    '@type': 'Service',
    '@id': `${siteUrl}/encuentratucoliving#service`,
    name: 'Búsqueda de Habitaciones, Coliving y Apartamentos',
    description: 'Plataforma para encontrar habitaciones en alquiler, coliving y apartamentos amueblados verificados en España. Alquiler flexible y temporal todo incluido.',
    provider: {
      '@id': `${siteUrl}/#organization`
    },
    serviceType: 'Real Estate Rental Service',
    areaServed: {
      '@type': 'Country',
      name: 'España'
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Digital Nomads, Students, Young Professionals'
    },
    category: 'Coliving',
    offers: {
      '@type': 'Offer',
      description: 'Acceso gratuito a la búsqueda de colivings',
      price: '0',
      priceCurrency: 'EUR'
    }
  };
  
  const realEstateAgencySchema = {
    '@type': 'RealEstateAgent',
    '@id': `${siteUrl}/#realestate`,
    name: 'Coolliving Real Estate Services',
    description: 'Servicios de búsqueda de habitaciones en alquiler, coliving y apartamentos amueblados en España',
    url: `${siteUrl}/encuentratucoliving`,
    telephone: '+34910000000',
    email: 'soporte@coolliving.io',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'ES',
      addressLocality: 'Madrid',
      addressRegion: 'Madrid'
    },
    serviceArea: {
      '@type': 'Country',
      name: 'España'
    }
  };
  
  return [orgSchema, serviceSchema, realEstateAgencySchema];
};

/**
 * Get local business schema para colivings por ciudades
 * @param {string} siteUrl - URL del sitio
 * @param {string} city - Ciudad específica
 * @returns {Object} Schema de LocalBusiness
 */
export const getColivingLocalBusinessSchema = (siteUrl, city = 'España') => {
  return {
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/encuentratucoliving#localbusiness-${city.toLowerCase()}`,
    name: city === 'Madrid'
      ? 'Coliving Madrid 2026: 1.400+ Habitaciones desde 450€/mes | Coolliving'
      : city === 'Barcelona'
        ? 'Coliving Barcelona 2026: 150+ Habitaciones desde 450€/mes | Coolliving'
        : city === 'London'
          ? 'Coliving London 2026: 3,000+ Furnished Rooms from £800/mo | Coolliving'
          : `Colivings en ${city} - Coolliving`,
    description: city === 'Madrid'
      ? 'Compara 1.400+ colivings en Madrid desde 450€/mes. Habitaciones verificadas en Malasaña, Chamberí y Lavapiés. Todo incluido.'
      : city === 'Barcelona'
        ? 'Coliving Barcelona 2026: 150+ habitaciones en Gràcia, Eixample, Poblenou y El Born desde 450€/mes. Todo incluido.'
        : city === 'London'
          ? 'Coliving London 2026: 3,000+ verified rooms in Shoreditch, Brixton, Camden and Wembley. From £800/mo all-inclusive, no agency fees.'
          : `Encuentra los mejores espacios de coliving en ${city}. Habitaciones verificadas, precios transparentes y proceso de reserva simplificado.`,
    url: `${siteUrl}/encuentratucoliving`,
    telephone: '+34910000000',
    email: 'soporte@coolliving.io',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'ES',
      addressLocality: city,
      addressRegion: city === 'Madrid' ? 'Madrid' : city === 'Barcelona' ? 'Cataluña' : city
    },
    geo: city === 'Madrid' ? {
      '@type': 'GeoCoordinates',
      latitude: '40.4168',
      longitude: '-3.7038'
    } : city === 'Barcelona' ? {
      '@type': 'GeoCoordinates', 
      latitude: '41.3851',
      longitude: '2.1734'
    } : undefined,
    openingHours: 'Mo-Su 00:00-23:59',
    serviceArea: {
      '@type': 'City',
      name: city
    }
  };
};

/**
 * Get FAQ schema específico para colivings (genérico)
 * @returns {Object} Schema de FAQPage para colivings
 */
export const getColivingFAQSchema = () => {
  const faqs = [
    {
      question: "¿Qué es un coliving y en qué se diferencia de un piso compartido?",
      answer: "Un coliving es un espacio de vivienda con habitación privada y zonas comunes compartidas (cocina, salón), con gestión profesional, contratos flexibles desde 1 mes y todo incluido. A diferencia de un piso compartido tradicional, no tienes que buscar compañeros ni gestionar facturas. Es ideal para jóvenes profesionales, estudiantes y nómadas digitales."
    },
    {
      question: "¿Qué tipos de alojamiento puedo encontrar en Coolliving?",
      answer: "En Coolliving comparas habitaciones en alquiler, colivings, apartamentos amueblados, flex living, residencias de estudiantes y pisos compartidos gestionados. Todos verificados, con alquiler flexible desde 1 mes y precios todo incluido."
    },
    {
      question: "¿Qué incluye el precio de un coliving o apartamento amueblado?",
      answer: "El precio todo incluido cubre la habitación privada amueblada, WiFi de alta velocidad, suministros (luz, agua, gas), limpieza de zonas comunes y uso de cocina y salón equipados. Algunos incluyen coworking, gimnasio y lavandería."
    },
    {
      question: "¿Qué es el alquiler flexible o flex living?",
      answer: "El alquiler flexible (flex living) permite alquilar habitaciones o apartamentos amueblados con contratos desde 1 mes, sin fianza abusiva y con todos los gastos incluidos. Es la alternativa moderna al alquiler temporal tradicional."
    },
    {
      question: "¿Cómo funciona el proceso de reserva?",
      answer: "1) Busca habitaciones y colivings por ciudad, precio o tipo, 2) Envía tu solicitud, 3) El operador revisa tu perfil, 4) Si te aceptan, firmas el contrato digitalmente, 5) ¡Listo! Todo online, sin comisiones para el inquilino."
    },
    {
      question: "¿Qué documentos necesito para alquilar una habitación?",
      answer: "Necesitas DNI o pasaporte, justificante de ingresos (nóminas, contrato de trabajo), y en algunos casos un avalista. Todo el proceso es digital y seguro a través de Coolliving."
    }
  ];

  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    })),
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.faq-answer', '.faq-question'],
    }
  };
};

/**
 * FAQs específicas por ciudad para mejorar SEO local
 */
const cityFAQs = {
  'Madrid': [
    {
      question: "¿Cuánto cuesta alquilar una habitación o coliving en Madrid?",
      answer: "El alquiler de habitaciones y colivings en Madrid va de 500€/mes a 900€/mes según la zona. Malasaña, Lavapiés y Moncloa desde 500€/mes; Salamanca y Chamberí premium desde 750€/mes. Todo incluido: habitación amueblada, WiFi, gastos y limpieza."
    },
    {
      question: "¿Hay habitaciones baratas en alquiler en Madrid por 500 euros?",
      answer: "Sí, hay habitaciones en alquiler y colivings en Madrid desde 500€/mes en Lavapiés, Vallecas y zonas bien conectadas en metro. El precio es todo incluido: habitación amueblada, WiFi, suministros y limpieza semanal. Usa el filtro de precio en Coolliving."
    },
    {
      question: "¿Cuáles son las mejores zonas para alquilar habitación en Madrid?",
      answer: "Las zonas más populares para alquiler de habitaciones y coliving en Madrid: Malasaña y Chueca (vida nocturna), Chamberí (tranquilo y céntrico), Salamanca (premium), Lavapiés (económico y multicultural) y Retiro. La elección depende de tu presupuesto."
    },
    {
      question: "¿Coliving, piso compartido o alquiler de habitación en Madrid?",
      answer: "El coliving ofrece gestión profesional, contratos flexibles desde 1 mes, todo incluido y comunidad. El piso compartido o alquiler de habitación tradicional puede ser más barato pero implica fianzas altas y gestionar facturas. En Coolliving comparas ambas opciones."
    },
    {
      question: "¿Hay apartamentos amueblados con alquiler flexible en Madrid?",
      answer: "Sí. Blueground, Muppy y otros operadores ofrecen apartamentos amueblados y flex living en Madrid con contratos desde 1 mes, todo incluido, sin fianza tradicional. Precios desde 800€/mes para estudios y apartamentos."
    },
    {
      question: "¿Qué incluye el precio de un coliving en Madrid?",
      answer: "Habitación privada amueblada, WiFi de alta velocidad, limpieza semanal de zonas comunes, suministros (luz, agua, gas) y uso de salón/cocina equipada. Muchos incluyen coworking, eventos de comunidad y mantenimiento 24/7."
    }
  ],
  'Barcelona': [
    {
      question: "¿Cuánto cuesta alquilar una habitación o coliving en Barcelona?",
      answer: "El alquiler de habitaciones y colivings en Barcelona va desde 450€/mes hasta 750€/mes. Eixample, Gràcia, Poblenou y El Born son las zonas más demandadas. Precio todo incluido: habitación amueblada, WiFi, suministros y limpieza."
    },
    {
      question: "¿Cuáles son las mejores zonas para alquilar habitación en Barcelona?",
      answer: "Las zonas más populares para alquiler de habitaciones: Eixample (céntrico), Gràcia (bohemio), Poblenou (zona tech y playa), El Born (cultura y gastronomía) y Sants (económico). Todas con coliving y flex living disponible."
    },
    {
      question: "¿Hay apartamentos amueblados y alquiler flexible en Barcelona?",
      answer: "Sí. En Barcelona hay apartamentos amueblados y flex living con contratos desde 1 mes. Spacest, Suiters y otros operadores ofrecen alquiler flexible todo incluido. También hay colivings cerca de la playa en Poblenou y Vila Olímpica desde 550€/mes."
    }
  ],
  'Valencia': [
    {
      question: "¿Cuánto cuesta alquilar una habitación en Valencia?",
      answer: "El alquiler de habitaciones y coliving en Valencia va desde 350€/mes hasta 550€/mes, la ciudad más asequible de España. Zonas populares: Ruzafa, El Carmen, Benimaclet y Ciutat Vella. Todo incluido."
    },
    {
      question: "¿Por qué Valencia es ideal para alquiler flexible?",
      answer: "Valencia combina clima mediterráneo, bajo coste de vida, playa y gastronomía. El alquiler flexible y el flex living son cada vez más populares: habitaciones y apartamentos amueblados desde 1 mes, sin fianzas. El destino preferido de nómadas digitales en España."
    },
    {
      question: "¿Hay habitaciones para estudiantes en Valencia?",
      answer: "Sí, Valencia tiene universidades reconocidas (UV, UPV) y una amplia oferta de habitaciones en alquiler, residencias y colivings para estudiantes desde 350€/mes. Benimaclet y Blasco Ibáñez son las zonas universitarias por excelencia."
    }
  ],
  'Sevilla': [
    {
      question: "¿Cuánto cuesta un coliving en Sevilla?",
      answer: "Los colivings en Sevilla tienen precios desde 300€/mes hasta 500€/mes, siendo una de las opciones más económicas en España. Zonas populares: Triana, Alameda, Macarena y el centro histórico."
    },
    {
      question: "¿Hay colivings en Sevilla para profesionales internacionales?",
      answer: "Sí, Sevilla atrae cada vez más a profesionales internacionales y nómadas digitales por su calidad de vida, clima excepcional y costes asequibles. Hay colivings con espacios de coworking y comunidad internacional."
    }
  ],
  'Málaga': [
    {
      question: "¿Cuánto cuesta alquilar una habitación o coliving en Málaga?",
      answer: "El alquiler de habitaciones y colivings en Málaga va desde 350€/mes hasta 600€/mes. Zonas populares: Centro Histórico, Soho, El Palo y Pedregalejo. También hay apartamentos amueblados con alquiler flexible desde 1 mes."
    },
    {
      question: "¿Hay alquiler flexible y flex living en Málaga?",
      answer: "Sí. Málaga es el destino número 1 de nómadas digitales en España. Ofrece habitaciones en alquiler, apartamentos amueblados y flex living con contratos desde 1 mes. 300+ días de sol, ecosistema tech (Google, Vodafone) y vuelos directos a toda Europa."
    }
  ],
  'Salamanca': [
    {
      question: "¿Cuánto cuesta un coliving en Salamanca?",
      answer: "Salamanca ofrece colivings desde 300€/mes hasta 500€/mes. Como ciudad universitaria por excelencia, tiene una gran oferta de habitaciones compartidas cerca del campus de la Universidad de Salamanca."
    },
    {
      question: "¿Es buen destino Salamanca para estudiantes Erasmus?",
      answer: "Salamanca es uno de los destinos Erasmus más populares de España. Su universidad es una de las más antiguas de Europa, y la ciudad tiene una vida estudiantil muy activa. Los colivings son ideales por su comunidad internacional."
    }
  ],
  'Alicante': [
    {
      question: "¿Cuánto cuesta un coliving en Alicante?",
      answer: "Los colivings en Alicante van desde 350€/mes hasta 550€/mes. Zonas populares: Centro, Playa de San Juan y El Campello. Alicante ofrece playa, clima excepcional y un coste de vida muy competitivo."
    }
  ],
  'Bilbao': [
    {
      question: "¿Cuánto cuesta un coliving en Bilbao?",
      answer: "Los colivings en Bilbao van desde 400€/mes hasta 650€/mes. Zonas populares: Casco Viejo, Indautxu, Deusto y Abando. Bilbao destaca por su gastronomía, cultura (Guggenheim) y calidad de vida."
    },
    {
      question: "¿Hay colivings en Bilbao para profesionales?",
      answer: "Sí, Bilbao tiene una creciente oferta de colivings orientados a profesionales, con espacios de coworking incluidos, WiFi de alta velocidad y comunidad internacional. La ciudad atrae cada vez más talento tech y creativo."
    }
  ],
  'San Sebastián': [
    {
      question: "¿Cuánto cuesta un coliving en San Sebastián?",
      answer: "Los colivings en San Sebastián (Donostia) van desde 400€/mes hasta 750€/mes. Zonas populares: Gros, Amara, Intxaurrondo y Centro. Todo incluido: WiFi, gastos y limpieza semanal."
    },
    {
      question: "¿Por qué elegir coliving en San Sebastián?",
      answer: "San Sebastián combina playas urbanas (La Concha, Zurriola), gastronomía de fama mundial, montaña a 20 minutos y una de las ciudades más seguras de España. Ideal para profesionales y nómadas digitales que buscan calidad de vida vasca."
    },
    {
      question: "¿Cuáles son los mejores barrios para coliving en Donostia?",
      answer: "Gros es el barrio más joven y vibrante; Amara ofrece buen precio y conexiones; Intxaurrondo es tranquilo con vistas panorámicas; el Centro para estar a pie de playa y pintxos."
    }
  ],
  'Granada': [
    {
      question: "¿Cuánto cuesta un coliving en Granada?",
      answer: "Los colivings en Granada van desde 300€/mes hasta 450€/mes, siendo una de las opciones más económicas en España. Zonas populares: Albaicín, Centro, Realejo y zona universitaria. Perfecta para estudiantes y nómadas digitales."
    },
    {
      question: "¿Es Granada buena ciudad para estudiantes en coliving?",
      answer: "Sí, Granada es una de las mejores ciudades universitarias de España, con la Universidad de Granada entre las más grandes del país. Los colivings cerca del campus ofrecen habitaciones desde 300€/mes con todo incluido y comunidad estudiantil activa."
    }
  ],
  'Zaragoza': [
    {
      question: "¿Cuánto cuesta un coliving en Zaragoza?",
      answer: "Los colivings en Zaragoza van desde 250€/mes hasta 450€/mes, con las opciones más asequibles de las grandes ciudades españolas. Zonas populares: Centro, Delicias, Universidad y el Casco Antiguo."
    },
    {
      question: "¿Por qué elegir Zaragoza para coliving?",
      answer: "Zaragoza ofrece una excelente relación calidad-precio, está estratégicamente ubicada entre Madrid y Barcelona (solo 1h15 en AVE a ambas), tiene universidad reconocida, rica gastronomía y un coste de vida muy competitivo."
    }
  ],

  // ── ENGLISH CITIES — US ──────────────────────────────────────────────────
  'New York': [
    {
      question: "How much does coliving in New York City cost?",
      answer: "Coliving in NYC ranges from $1,200/mo in outer boroughs (Queens, Brooklyn) to $3,500/mo in prime Manhattan. The average is around $1,900/mo all-inclusive (WiFi, utilities, weekly cleaning). No broker fee platforms like June Homes, Common and Roomrs can save you $2,000–$4,000 compared to signing a traditional lease."
    },
    {
      question: "What is the best neighbourhood for coliving in NYC?",
      answer: "Manhattan (Midtown, Chelsea, UWS) is best for proximity to work and nightlife. Brooklyn (Williamsburg, Bushwick, Crown Heights) offers a creative community at lower cost. Queens (Astoria, Long Island City) is the most affordable with fast subway to Midtown. Jersey City gives excellent value with a 5-min PATH train to Lower Manhattan."
    },
    {
      question: "Is there a broker fee for coliving in New York?",
      answer: "No — most coliving platforms (June Homes, Common, Roomrs, SharedEasy, Blueground) charge zero broker fee. Traditional NYC rentals often require paying 1 month's rent (typically $3,000+) as a broker fee. Coliving saves you that upfront cost."
    },
    {
      question: "What is included in NYC coliving prices?",
      answer: "All-inclusive NYC coliving typically covers your private furnished room, utilities (electric, gas, water), high-speed WiFi, weekly common area cleaning and often a shared cleaner for your apartment. Some include amenities like gym access, rooftop and coworking desks. No security deposit surprises."
    },
    {
      question: "What is the minimum stay for coliving in NYC?",
      answer: "Most NYC coliving operators offer flexible leases from 1 month, with some offering week-to-week or 30-day minimums. This is ideal for corporate relocations, internships, grad students and remote workers who need flexibility without a 12-month lease commitment."
    }
  ],
  'Manhattan': [
    {
      question: "How much is coliving in Manhattan?",
      answer: "Coliving in Manhattan ranges from $1,500/mo for a room in a shared apartment in Washington Heights or Inwood to $3,500/mo in prime Midtown, Chelsea or the Upper West Side. The average for a well-located private room is around $2,200/mo all-inclusive."
    },
    {
      question: "Is coliving worth it in Manhattan vs renting an apartment?",
      answer: "Yes — a studio in Manhattan averages $3,500–$5,000/mo plus a broker fee of $3,000–$5,000. Coliving gives you a furnished private room, utilities, WiFi and community from $1,800/mo with no broker fee. You save $1,000–$3,000/mo and avoid the stress of furnishing and setting up utilities."
    },
    {
      question: "What neighbourhoods are best for coliving in Manhattan?",
      answer: "Midtown is central for business travel and commuting to major offices. Chelsea and Hell's Kitchen are popular with young professionals. The Upper West Side is quieter and family-friendly. Washington Heights and Inwood offer the best value. East Village and Lower East Side are ideal for nightlife and culture."
    }
  ],
  'Brooklyn': [
    {
      question: "How much does coliving in Brooklyn cost?",
      answer: "Coliving in Brooklyn ranges from $1,200/mo in East New York, Canarsie or Flatbush to $2,500/mo in Williamsburg, DUMBO or Park Slope. The average for a private furnished room all-inclusive is $1,700/mo — typically $500–$1,000 cheaper than equivalent Manhattan options."
    },
    {
      question: "What are the best neighbourhoods for coliving in Brooklyn?",
      answer: "Williamsburg is the top choice for young professionals: great restaurants, bars, art scene and L/J/M subway lines. Bushwick is more affordable and equally creative. Crown Heights and Prospect Lefferts Gardens offer great value. DUMBO is premium with Manhattan Bridge views. Greenpoint is popular with tech workers."
    },
    {
      question: "How long is the commute from Brooklyn to Manhattan?",
      answer: "Commute depends on neighbourhood: DUMBO/Brooklyn Heights to Lower Manhattan is 10–15 min by subway. Williamsburg to Midtown is 25–35 min. Crown Heights to Midtown is 35–45 min. Park Slope to Midtown is 30–40 min. Most coliving in Brooklyn is under 45 min to major Manhattan employment centres."
    },
    {
      question: "Is coliving in Brooklyn cheaper than Manhattan with no broker fee?",
      answer: "Yes. Brooklyn coliving averages about $1,700/mo all-inclusive vs $2,200+ in Manhattan for a comparable furnished room — and Coolliving listings have zero broker fee (traditional NYC leases often charge one month's rent, $2,400–$4,000+). Month-to-month coliving in Brooklyn is one of the most popular ways to avoid both broker fees and 12-month leases."
    }
  ],
  'Queens': [
    {
      question: "How much does coliving in Queens NYC cost?",
      answer: "Coliving in Queens is typically the most affordable in NYC, ranging from $1,200/mo in Jamaica, South Jamaica or Far Rockaway to $2,200/mo in Astoria or Long Island City. The average for a private furnished room all-inclusive is $1,500/mo — often $500–$1,000 cheaper than Brooklyn."
    },
    {
      question: "What are the best areas for coliving in Queens?",
      answer: "Long Island City (LIC) is the best connected: E/M/7 to Midtown in 10 min. Astoria is a top choice for quality of life: great food, Greek community and N/W trains. Flushing is vibrant and multicultural. Jackson Heights is diverse and affordable. Sunnyside and Woodside are quiet residential areas with great value."
    },
    {
      question: "Is Queens good for digital nomads and remote workers?",
      answer: "Yes — Queens offers a unique combination of international culture (the most diverse county in the US), affordable housing, good internet and proximity to two airports (JFK and LaGuardia). Astoria and Long Island City have coworking spaces. Many coliving operators in Queens offer fast WiFi and dedicated workspaces."
    }
  ],
  'Jersey City': [
    {
      question: "Is coliving in Jersey City cheaper than NYC?",
      answer: "Yes — significantly. Private rooms in Jersey City coliving range from $1,100–$1,900/mo vs $1,500–$3,000/mo for comparable options in Manhattan or Brooklyn. With a PATH train commute of just 5–10 minutes to Lower Manhattan and 20 minutes to Midtown, Jersey City offers exceptional value."
    },
    {
      question: "How is the commute from Jersey City to Manhattan?",
      answer: "Excellent. The PATH train runs 24/7 from Grove Street, Journal Square or Newport to World Trade Center in 5 minutes and to Midtown (33rd St) in 20 minutes. Some coliving buildings are steps from PATH stations. Many residents prefer Jersey City over Brooklyn for the faster, cheaper commute to Lower Manhattan."
    },
    {
      question: "Can I get month-to-month coliving in Jersey City?",
      answer: "Most Jersey City coliving operators on Coolliving offer flexible leases from 1 month with furnished private rooms, utilities and WiFi included — no broker fee. Ideal for finance and tech workers testing a NYC-area commute before committing to a long lease."
    }
  ],

  // ── ENGLISH CITIES — UK ──────────────────────────────────────────────────
  'London': [
    {
      question: "How much does coliving in London cost?",
      answer: "Coliving in London ranges from £860/mo in outer zones (Epsom, Hayes, New Malden) to £3,100/mo in premium Central London (Royal Docks, Poplar). The average for a well-located all-inclusive private room is around £1,400/mo. Compare that to a studio flat which averages £1,800–£2,500/mo before bills."
    },
    {
      question: "What is the best area to colive in London?",
      answer: "East London (Shoreditch, Hackney, Canary Wharf) is best for tech workers and creatives. Wembley Park offers the best value with fast tube access. South London (Brixton, Elephant & Castle, Clapham) is popular with young professionals. Bloomsbury and Kings Cross are ideal for students near UCL, LSE and Central Saint Martins."
    },
    {
      question: "Are there agency fees for coliving in London?",
      answer: "No — all major coliving operators in London (Ark Living, Lyvly, Fizzy Living, Vonder, Chapter, Gravity Co) charge zero agency fees. Traditional letting agents charge 1–2 weeks' rent (£350–£700+). With coliving you pay one all-inclusive monthly fee covering room, bills, WiFi and cleaning."
    },
    {
      question: "What does an all-inclusive London coliving price cover?",
      answer: "Typically: a private furnished en-suite room, all utilities (gas, electricity, water), fast WiFi (100+ Mbps), weekly cleaning of communal areas and often bedroom cleaning. Many London coliving buildings also include gym access, coworking desks, bike storage and regular community events."
    },
    {
      question: "What is the minimum stay for coliving in London?",
      answer: "Most London coliving operators offer leases from 1 to 3 months minimum. Fizzy Living, Ark Living and Lyvly typically have 3-month minimums. Vonder and Mason & Fifth offer shorter flex stays. Chapter Living aligns with academic terms (September–June) for students. Long-term stays (6–12 months) get discounts at most providers."
    }
  ],
  'Shoreditch': [
    {
      question: "How much does coliving in Shoreditch cost?",
      answer: "Coliving in Shoreditch ranges from £1,200/mo (Ark Living shared rooms) to £1,880/mo (Vonder private studios). The area is London's tech and creative hub — home to Silicon Roundabout, hundreds of startups and a vibrant arts and food scene. Despite premium demand, coliving is significantly cheaper than renting a studio (avg £2,200/mo in E1/E2)."
    },
    {
      question: "Why choose Shoreditch for coliving in London?",
      answer: "Shoreditch is London's most vibrant coliving destination: Old Street (Silicon Roundabout) is Europe's biggest tech cluster, Brick Lane and Spitalfields are steps away, and the area has the best bars, street food and gallery scene in London. Central line from Liverpool Street in 2 minutes, Elizabeth line to Paddington in 10 minutes."
    }
  ],
  'Brixton': [
    {
      question: "How much is coliving in Brixton, London?",
      answer: "Coliving in Brixton ranges from £1,095/mo (Lyvly shared houses) to £2,562/mo (Node Living premium studios). Brixton is one of London's most exciting neighbourhoods: Brixton Market, live music venues, diverse food scene and excellent transport — Victoria line to Oxford Circus in 10 minutes."
    },
    {
      question: "Is Brixton a good area for coliving in London?",
      answer: "Absolutely. Brixton has transformed into one of London's most desirable areas for young professionals. It offers a vibrant cultural scene, excellent transport (Victoria line to Victoria in 8 min), great cafes and restaurants, and lower prices than equivalent North or East London locations."
    }
  ],
  'Islington': [
    {
      question: "How much does coliving in Islington cost?",
      answer: "Coliving in Islington (including Highbury) ranges from £1,446/mo to £1,663/mo for Chapter Living private rooms. Islington is Zone 2, 5 minutes from Caledonian Road Piccadilly line and well connected to the City. Upper Street, Angel and the Holloway Road area offer plenty of restaurants, bars and coworking cafes."
    },
    {
      question: "Why choose Islington for student coliving in London?",
      answer: "Islington is perfectly positioned for students at UAL Central Saint Martins (3 stops), City University London (1 stop) and UCL (20 min). Chapter Living operates purpose-built student accommodation in Islington with gym, study spaces, art room and access to all 15 Chapter London buildings via Chapter Club."
    }
  ],
  'Camden': [
    {
      question: "How much is coliving in Camden, London?",
      answer: "Coliving in Camden ranges from £1,600/mo to £2,200/mo for a private furnished room all-inclusive (WiFi, bills, weekly cleaning), with no agency fees. Camden is Zone 2, with Northern line connections to King's Cross (8 min), Euston (5 min) and the City (15-20 min). Premium location with iconic lifestyle — significantly cheaper than renting a studio in the area (avg £2,000-£2,500/mo)."
    },
    {
      question: "Is Camden good for young professionals?",
      answer: "Yes. Camden attracts creative professionals, tech workers, musicians and media industry professionals. Northern line to King's Cross (Google HQ, tech hub) in 8 minutes. Vibrant nightlife and cultural scene at Camden Market and Electric Ballroom. Close to Regent's Park for outdoor activities. Strong international community."
    },
    {
      question: "What is Camden like as a place to live?",
      answer: "Camden is one of London's most iconic and characterful neighbourhoods — never boring. Camden Market, dozens of live music venues, independent restaurants and the Regent's Canal towpath make it exceptionally liveable. Primrose Hill (15 min walk) has some of London's best views. Friendly, creative community that draws people from all over the world."
    }
  ],
  'Williamsburg': [
    {
      question: "How much does coliving in Williamsburg, Brooklyn cost?",
      answer: "Coliving in Williamsburg ranges from $1,800/mo in South or East Williamsburg to $3,200/mo for premium waterfront rooms with Manhattan skyline views. The average for a private furnished room all-inclusive (utilities, WiFi, weekly cleaning) is around $2,400/mo, with no broker fee. Significantly cheaper than renting a Williamsburg studio ($3,000-$4,500/mo on the open market)."
    },
    {
      question: "How do I get from Williamsburg to Manhattan?",
      answer: "L train from Bedford Avenue to 14th St-Union Square: 8 minutes. To Times Square (Midtown): 20 minutes. J/M/Z from Marcy Avenue to Fulton Street (Downtown): 12 minutes. NYC Ferry from N 7th St Pier to Wall Street: 25 minutes. One of the best-connected Brooklyn neighborhoods for Manhattan commuters."
    },
    {
      question: "Is Williamsburg good for tech workers and creatives?",
      answer: "Williamsburg is Brooklyn's most vibrant creative and professional hub — world-class restaurants, rooftop bars, art galleries and a community that draws developers, designers, founders and musicians from around the world. Many startups and studios are based in Williamsburg and nearby DUMBO. Fast L train access to Manhattan tech companies."
    }
  ],
  'Astoria': [
    {
      question: "How much is coliving in Astoria, Queens NYC?",
      answer: "Coliving in Astoria ranges from $1,400/mo in the Steinway or south Astoria area to $2,400/mo near the waterfront or Ditmars Boulevard. The average for a private furnished room all-inclusive (utilities, WiFi, cleaning) is $1,800/mo — no broker fee. One of the most affordable and well-connected options in the entire NYC metropolitan area."
    },
    {
      question: "How long is the commute from Astoria to Manhattan?",
      answer: "N or W train from Astoria-Ditmars Blvd to Times Square (Midtown): 20-25 minutes. To 57th St-7th Ave: 22 minutes. To Grand Central: 25 minutes. All direct with no transfers. The N/W trains run frequently. Many Astoria residents find commute times comparable to inner Brooklyn but at significantly lower cost."
    },
    {
      question: "Why is Astoria a great place to colive in NYC?",
      answer: "Astoria offers NYC's best quality-of-life-to-price ratio: affordable rooms, diverse and vibrant restaurant scene (best Greek food in the US, plus Italian, Middle Eastern, South Asian), Astoria Park on the East River, safe neighbourhoods and fast Manhattan access. Very popular with international professionals, remote workers and those who value community over nightlife."
    }
  ]
};

/**
 * Get FAQ schema específico para una ciudad
 * Combina las FAQs genéricas con FAQs específicas de la ciudad
 * @param {string} city - Nombre de la ciudad
 * @returns {Object} Schema de FAQPage con FAQs genéricas + de ciudad
 */
export const getColivingCityFAQSchema = (city, options = {}) => {
  const { isStudentMode = false } = options;

  if (isStudentMode && cityInfo[city]?.studentFaqs?.length) {
    return {
      '@type': 'FAQPage',
      mainEntity: cityInfo[city].studentFaqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    };
  }

  const genericFaqs = [
    {
      question: "¿Qué tipos de alojamiento puedo encontrar en Coolliving?",
      answer: "En Coolliving comparas habitaciones en alquiler, colivings, apartamentos amueblados, residencias de estudiantes y flex living. Todo verificado, con alquiler flexible desde 1 mes, precios todo incluido y sin comisiones para el inquilino."
    },
    {
      question: "¿Qué incluye el precio de un coliving o habitación en alquiler?",
      answer: "El precio todo incluido cubre habitación privada amueblada, WiFi de alta velocidad, suministros (luz, agua, gas), limpieza de zonas comunes y uso de cocina y salón equipados. Muchos incluyen coworking y lavandería."
    },
    {
      question: "¿Cómo funciona el alquiler flexible en Coolliving?",
      answer: "Busca habitaciones y colivings por ciudad, precio o tipo. Envía tu solicitud online, el operador la revisa, firmas contrato digital y listo. Contratos desde 1 mes, sin fianza abusiva."
    }
  ];

  // Combinar FAQs genéricas con las específicas de la ciudad
  const specificFaqs = cityFAQs[city] || [];
  const allFaqs = [...specificFaqs, ...genericFaqs];

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: allFaqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    })),
  };

  // speakable: marks FAQ answers as suitable for voice assistants (Google Assistant)
  // Only add for cities with specific FAQs (curated, high-quality answers)
  if (specificFaqs.length > 0) {
    faqSchema.speakable = {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.faq-answer', '.faq-question'],
    };
  }

  return faqSchema;
};

function getStudentHubFAQSchema() {
  const faqs = [
    {
      question: '¿Qué es una residencia universitaria de operador?',
      answer: 'Son residencias gestionadas por un operador profesional (RESA, Nido, Xior, Livensa…): habitación privada o en suite, zonas comunes, WiFi y servicios incluidos en una sola factura. No son pisos compartidos entre particulares.',
    },
    {
      question: '¿Qué ciudades tienen residencias universitarias en Coolliving?',
      answer: 'Madrid, Barcelona, Valencia, Málaga, Sevilla y Salamanca. En cada ciudad comparas solo operadores verificados (RESA, Nido, Xior, Livensa, Collegiate, Yugo, Urban Campus).',
    },
    {
      question: '¿Cuánto cuesta una residencia de estudiantes en España?',
      answer: 'Desde 320€/mes en Salamanca hasta 500€/mes en Madrid, todo incluido según ciudad y habitación. Barcelona, Valencia y Málaga suelen estar entre 380€ y 480€/mes.',
    },
    {
      question: '¿Puedo reservar solo un semestre Erasmus?',
      answer: 'Depende del operador: algunas residencias universitarias exigen curso completo; otras aceptan 5-9 meses. Revisa el mínimo de estancia en cada ficha antes de solicitar en Coolliving.',
    },
    {
      question: '¿En qué se diferencia /residencias de /encuentratucoliving?',
      answer: '/residencias muestra solo operadores verificados para estudiantes. /encuentratucoliving incluye también colivings flex y apartamentos para nómadas y profesionales.',
    },
  ];

  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Get breadcrumb schema para la página de búsqueda
 * @param {string} siteUrl - URL base del sitio
 * @param {string} location - Ubicación específica (opcional)
 * @returns {Object} Esquema de BreadcrumbList
 */
export const getColivingSearchBreadcrumbSchema = (siteUrl, location = null, options = {}) => {
  const { isStudentMode = false } = options;
  const items = [
    { name: 'Inicio', url: '/' },
    {
      name: isStudentMode ? 'Residencias universitarias' : 'Encuentra tu Coliving',
      url: isStudentMode ? '/residencias' : '/encuentratucoliving',
    },
  ];

  if (location) {
    const normalizedLocation = location.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');
    const studentNames = {
      Madrid: 'Residencia Estudiantes Madrid',
      Barcelona: 'Residencia Estudiantes Barcelona',
    };
    items.push({
      name: isStudentMode && studentNames[location]
        ? studentNames[location]
        : location === 'Madrid'
          ? 'Coliving Madrid'
          : location === 'Barcelona'
            ? 'Coliving Barcelona'
            : location === 'London'
              ? 'Coliving London'
              : `Colivings en ${location}`,
      url: isStudentMode
        ? `/residencias/${normalizedLocation}`
        : location === 'London'
          ? `/findyourcoliving/london`
          : `/encuentratucoliving/${normalizedLocation}`,
    });
  }

  return {
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`
    }))
  };
};

/**
 * Get ItemList schema para resultados de colivings
 * Solo se genera para ciudades principales y cuando hay resultados
 * @param {string} siteUrl - URL base del sitio
 * @param {Object} options - Opciones
 * @param {Array} options.results - Array de resultados de colivings
 * @param {string} options.location - Ciudad específica
 * @param {number} options.currentPage - Página actual (default 1)
 * @returns {Object|null} Schema de ItemList o null
 */
const UK_CITIES_SET = new Set(LONDON_NEIGHBORHOODS);

const US_CITIES_SET = new Set([
  'New York', 'Manhattan', 'Brooklyn', 'Queens', 'Jersey City', 'Williamsburg', 'Astoria'
]);

export const getColivingItemListSchema = (siteUrl, options = {}) => {
  const { results, location, currentPage = 1, totalResults = 0, isStudentMode = false } = options;

  // Ciudades ES + barrios US/UK prerenderizados (ItemList cacheable por Google)
  const allowedCities = new Set([
    'Madrid', 'Barcelona', 'Valencia', 'Málaga', 'Malaga',
    'Sevilla', 'Tenerife', 'Las Palmas', 'Salamanca',
    'Bilbao', 'San Sebastián', 'Granada', 'Zaragoza', 'Alicante',
    ...US_CITIES_SET,
    ...UK_CITIES_SET,
  ]);
  
  // Solo generar si:
  // 1. Hay resultados
  // 2. Es una ciudad permitida
  // 3. Hay al menos 1 resultado
  if (!results || results.length === 0) return null;
  if (!location || !allowedCities.has(location)) return null;
  
  // Limitar a primeros 10 resultados (Google recomienda max 10-20)
  const limitedResults = results.slice(0, 10);
  const totalListed = totalResults > 0 ? totalResults : limitedResults.length;
  const searchBase = isStudentMode
    ? 'residencias'
    : (UK_CITIES_SET.has(location) || US_CITIES_SET.has(location) ? 'findyourcoliving' : 'encuentratucoliving');
  const locationSlug = location.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');

  return {
    '@type': 'ItemList',
    'name': isStudentMode
      ? (location ? `Residencias universitarias en ${location}` : 'Residencias universitarias en España')
      : (location ? `Coliving in ${location}` : 'Coliving search results'),
    'url': `${siteUrl}/${searchBase}/${locationSlug}`,
    'numberOfItems': totalListed,
    'itemListElement': limitedResults.map((coliving, index) => {
      // Generar slug seguro para la URL
      const fichaPath = coliving.isExternal && coliving.externalSource && coliving.externalId
        ? `/colivings/${String(coliving.externalSource).toLowerCase()}/${coliving.externalId}`
        : coliving.id
          ? `/colivings/${coliving.id}`
          : `/colivings/${coliving.slug || coliving.externalId || `coliving-${index}`}`;
      
      // Precio seguro (mínimo 0)
      const price = coliving.priceFrom || coliving.propertyPrice || 0;
      
      // Nombre seguro
      const name = coliving.title || 
                   coliving.propertyName || 
                   `Coliving en ${location}`;
      
      // Descripción segura
      const description = coliving.shortDescription || 
                         coliving.description?.substring(0, 200) ||
                         `Habitación privada en coliving en ${location}`;
      
      // Imagen segura
      const image = coliving.mainImage || 
                    coliving.images?.[0] || 
                    `${siteUrl}/images/default-coliving.jpg`;
      
      const currency = US_CITIES_SET.has(location) ? 'USD' : UK_CITIES_SET.has(location) ? 'GBP' : 'EUR';
      const addressCountry = US_CITIES_SET.has(location) ? 'US' : UK_CITIES_SET.has(location) ? 'GB' : 'ES';
      const vecyRating = coliving.vecyRating || coliving.vecyScore;

      const accommodation = {
        '@type': 'Accommodation',
        '@id': `${siteUrl}${fichaPath}`,
        'name': name,
        'description': description,
        'image': image,
        'offers': {
          '@type': 'Offer',
          'price': price.toString(),
          'priceCurrency': currency,
          'availability': coliving.assignedApplicant
            ? 'https://schema.org/OutOfStock'
            : 'https://schema.org/InStock',
          'url': `${siteUrl}${fichaPath}`
        },
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': coliving.city || location,
          'addressCountry': addressCountry
        }
      };

      if (vecyRating?.score && vecyRating?.reviewCount) {
        accommodation.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: vecyRating.score.toString(),
          reviewCount: vecyRating.reviewCount.toString(),
          bestRating: '5',
          worstRating: '1',
        };
      }

      return {
        '@type': 'ListItem',
        'position': (currentPage - 1) * 10 + index + 1,
        'item': accommodation
      };
    })
  };
};

/**
 * Get Offer schema con rangos de precios dinámicos por ciudad
 * @param {string} location - Ciudad específica (opcional)
 * @returns {Object} Schema de Offer con precios
 */
export const getColivingOfferSchema = (location = null) => {
  // Precios por ciudad — fuente única: shared/citySeoData.cjs
  // getCityPriceRange ya devuelve { min, max } con fallback { min: 350, max: 800 }
  const normalizedLoc = location === 'Malaga' ? 'Málaga' : location;
  const prices = getCityPriceRange(normalizedLoc);
  const citySeo = getCitySeo(normalizedLoc);
  const currency = citySeo?.currency || 'EUR';

  // URL base según si es US/UK (findyourcoliving) o ES (encuentratucoliving)
  const isEnglishCity = currency === 'USD' || currency === 'GBP';
  const searchBase = isEnglishCity ? 'findyourcoliving' : 'encuentratucoliving';

  return {
    '@type': 'AggregateOffer',
    'priceCurrency': currency,
    'lowPrice': prices.min.toString(),
    'highPrice': prices.max.toString(),
    'offerCount': location ? '50' : '1100',
    'availability': 'https://schema.org/InStock',
    'priceValidUntil': new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +90 días
    'url': location 
      ? `https://coolliving.io/${searchBase}/${location.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-')}`
      : 'https://coolliving.io/encuentratucoliving'
  };
};

/**
 * Schema completo para la página de búsqueda de colivings
 * @param {string} siteUrl - URL del sitio
 * @param {Object} options - Opciones adicionales
 * @param {string} options.location - Ubicación específica
 * @param {number} options.resultsCount - Número de resultados
 * @param {boolean} options.isCitySpecific - Si es una página específica de ciudad
 * @param {Array} options.results - Array de resultados para ItemList (opcional)
 * @param {number} options.currentPage - Página actual para ItemList (opcional)
 * @param {boolean} options.isStudentMode - Página /residencias-estudiantes
 * @returns {Array<Object>} Schema completo
 */
export const getColivingSearchPageSchema = (siteUrl, options = {}) => {
  const {
    location,
    resultsCount,
    isCitySpecific,
    results,
    currentPage,
    totalResults,
    isStudentMode = false,
  } = options;
  
  // Usar FAQ de ciudad si es una página específica de ciudad, sino FAQ genérica
  const faqSchema = (isCitySpecific && location)
    ? getColivingCityFAQSchema(location, { isStudentMode })
    : (isStudentMode ? getStudentHubFAQSchema() : getColivingFAQSchema());
  
  // Para ciudades UK/US ajustar el WebSite potentialAction URL
  const isEnglishPage = US_CITIES_SET.has(location) || UK_CITIES_SET.has(location);
  
  const schemas = [
    ...getColivingSearchWebsiteSchema(siteUrl),
    ...getColivingServiceSchema(siteUrl),
    faqSchema,
    getColivingSearchBreadcrumbSchema(siteUrl, location, { isStudentMode })
  ];

  // Añadir schema de negocio local si hay ubicación específica
  if (location) {
    schemas.push(getColivingLocalBusinessSchema(siteUrl, location));
  }

  // NUEVO: Añadir ItemList schema si hay resultados y es una ciudad permitida
  const itemListSchema = getColivingItemListSchema(siteUrl, {
    results,
    location,
    currentPage,
    totalResults,
    isStudentMode,
  });
  
  if (itemListSchema) {
    schemas.push(itemListSchema);
  }

  // NUEVO: Añadir Offer con precios dinámicos por ciudad
  const offerSchema = getColivingOfferSchema(location);

  // URL específica según si es página de ciudad o búsqueda general
  const searchBase = isStudentMode
    ? 'residencias'
    : (isEnglishPage ? 'findyourcoliving' : 'encuentratucoliving');
  const locationSlug = location ? location.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-') : '';
  const pageUrl = isCitySpecific && location 
    ? `${siteUrl}/${searchBase}/${locationSlug}`
    : `${siteUrl}/${searchBase}`;

  const studentPageNames = {
    Madrid: 'Residencias Universitarias Madrid 2026 — RESA, Nido, Xior | Coolliving',
    Barcelona: 'Residencias Universitarias Barcelona 2026 — Livensa, RESA | Coolliving',
    Valencia: 'Residencias Universitarias Valencia 2026 — UV, UPV | Coolliving',
    Málaga: 'Residencias Universitarias Málaga 2026 — UMA | Coolliving',
    Malaga: 'Residencias Universitarias Málaga 2026 — UMA | Coolliving',
    Sevilla: 'Residencias Universitarias Sevilla 2026 — US | Coolliving',
    Salamanca: 'Residencias Universitarias Salamanca 2026 — USAL | Coolliving',
  };
  const studentPageDescriptions = {
    Madrid: 'Residencias universitarias en Madrid: RESA, Nido, Xior, Livensa, Collegiate, Yugo y Urban Campus. Desde 500€/mes todo incluido. Reserva online sin comisiones.',
    Barcelona: 'Residencias universitarias en Barcelona cerca de UB y UPF. Livensa, RESA, Nido, Xior. Desde 450€/mes todo incluido.',
    Valencia: 'Residencias universitarias en Valencia para UV y UPV. Desde 380€/mes todo incluido. Operadores verificados.',
    Málaga: 'Residencias universitarias en Málaga (UMA). Desde 400€/mes todo incluido.',
    Malaga: 'Residencias universitarias en Málaga (UMA). Desde 400€/mes todo incluido.',
    Sevilla: 'Residencias universitarias en Sevilla. Desde 350€/mes todo incluido. US y Pablo de Olavide.',
    Salamanca: 'Residencias universitarias en Salamanca para USAL. Desde 320€/mes todo incluido.',
  };

  // Schema de página web específica con rating y offers integrados
  const webPageSchema = {
    '@type': 'WebPage',
    '@id': pageUrl,
    url: pageUrl,
    name: location
      ? (isStudentMode && studentPageNames[location]
          ? studentPageNames[location]
          : isEnglishPage
          ? (location === 'London'
              ? 'Coliving London 2026: 3,000+ Furnished Rooms from £800/mo | Coolliving'
              : `${resultsCount || 'Best'} Coliving in ${location} — No Agency/Broker Fee | Coolliving`)
          : location === 'Madrid'
            ? 'Coliving Madrid 2026: 1.400+ Habitaciones desde 450€/mes | Coolliving'
            : location === 'Barcelona'
              ? 'Coliving Barcelona 2026: 150+ Habitaciones desde 450€/mes | Coolliving'
              : `Colivings en ${location} - Encuentra tu Espacio Perfecto`)
      : (isStudentMode
          ? 'Residencias universitarias España 2026 — RESA, Nido, Xior | Coolliving'
          : 'Habitaciones, Coliving y Apartamentos Amueblados en España - Coolliving'),
    description: location
      ? (isStudentMode && studentPageDescriptions[location]
          ? studentPageDescriptions[location]
          : isEnglishPage
          ? (location === 'London'
              ? 'Coliving London 2026: 3,000+ verified rooms in Shoreditch, Brixton, Camden and Wembley. From £800/mo all-inclusive, no agency fees.'
              : `Find ${resultsCount || 'top'} coliving rooms in ${location}. Verified listings, all-inclusive pricing, no broker or agency fee. Apply online today.`)
          : location === 'Madrid'
            ? 'Coliving Madrid 2026: 1.400+ habitaciones en Malasaña, Chamberí y Lavapiés desde 450€/mes. Todo incluido.'
            : location === 'Barcelona'
              ? 'Coliving Barcelona 2026: 150+ habitaciones en Gràcia, Eixample, Poblenou y El Born desde 450€/mes. Todo incluido.'
              : `Descubre ${resultsCount || 'los mejores'} colivings en ${location}. Habitaciones verificadas, precios transparentes. ¡Encuentra tu hogar ideal!`)
      : (isStudentMode
          ? 'Residencias universitarias en Madrid, Barcelona, Valencia, Málaga, Sevilla y Salamanca. Compara RESA, Nido, Xior, Livensa y más. Desde 320€/mes todo incluido.'
          : 'Busca habitaciones en alquiler, coliving y apartamentos amueblados en España. Miles verificados, alquiler flexible todo incluido, sin comisiones.'),
    isPartOf: {
      '@id': `${siteUrl}/#website`
    },
    about: {
      '@type': 'Thing',
      name: 'Alquiler de habitaciones, coliving y apartamentos amueblados',
      description: 'Habitaciones en alquiler, coliving, apartamentos amueblados y alquiler flexible para jóvenes profesionales, estudiantes y nómadas digitales'
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Young Professionals, Students, Digital Nomads, Remote Workers'
    },
    inLanguage: isEnglishPage ? 'en' : 'es-ES',
    // AggregateRating no va en WebPage — GSC: "parent_node no válido" en fragmentos de reseña.
    // Solo tipos como LocalBusiness, Product, Organization (con reseñas reales visibles) lo admiten.
    offers: offerSchema,
    // Añadir información geográfica específica si es una página de ciudad
    ...(isCitySpecific && location && {
      spatialCoverage: {
        '@type': 'City',
        name: location,
        addressCountry: 'ES'
      }
    })
  };

  schemas.push(webPageSchema);

  return schemas;
};
